import type { ReactNode } from 'react';

// ------------------------------------------------------------------
// GalleryPage — story 2-147p (P10 §4, DOD). One of six gallery-scaffolding
// components: the /app/design gallery's OWN presentation layer, formalizing
// markup that was previously hand-built directly inside
// `frontend/src/pages/DesignSystemPage.tsx` (a bare
// `<div className="space-y-6"><header>...` the page's own return statement
// opened with) rather than a named, reusable component.
//
// Lives in `components/gallery/`, not `components/ui/` — this is NOT a
// product-page primitive (P10 §2.7's canonical matrix has no row for it; a
// product page never composes a gallery page shell). It is the gallery's
// own internal scaffolding, the same status
// GallerySection/ExampleFrame/VariantGrid/StateMatrix/ResponsivePreview get
// in this story. See this story's SESSION-HANDOFF.md entry for the full
// "why a new components/gallery/ home, not components/ui/" reasoning,
// including why these six files are invisible to
// `frontend/scripts/census/ui-registry.json` (2-147n's registry, not
// re-run by this story) and therefore to the layer-aware ESLint plugin
// (2-147xa) — a real, named, non-blocking gap for whichever story next
// re-syncs the registry, not silently hidden.
// ------------------------------------------------------------------

export interface GalleryPageProps {
  /** Page title — renders as the gallery's single <h1>. */
  title: string;
  /** Optional prose under the title (may include inline markup, e.g. <code>). */
  description?: ReactNode;
  /** The gallery's own Section stack. */
  children: ReactNode;
}

export function GalleryPage({ title, description, children }: GalleryPageProps): JSX.Element {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-foreground">{title}</h1>
        {description && <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{description}</p>}
      </header>
      {children}
    </div>
  );
}
